/**
 * A linha de `ModalidadesPeriodosGarantidos` como chega pelo
 * `ReservedScheduleModalitiesFragment` — os campos de regra criados na KRA-523.
 */
interface ModalityCatalogRow {
  codModalidadePeriodoGarantido: number;
  /** Formato do Hasura para `time`: `"04:00:00"`. */
  duracaoMinimaDoPeriodo: string;
  permiteFrequenciaQuinzenal: boolean;
  multiplicadorPreco: number;
}

export interface ReservedScheduleModalityRules {
  /** No formato do campo de duração do formulário: `"04:00"`. */
  minimumDuration: string;
  /**
   * `minimumDuration` já partida, para a roleta de duração: a hora é o piso da coluna de horas e
   * os dois são o valor inicial. Os minutos **não** vão para o piso da coluna de minutos — ela não
   * acopla minutos à hora, e `minMinutes: 30` deixaria `[30]` como única opção. Quem barra `01:00`
   * numa categoria de `01:30` é `reservedScheduleBasicInfoWithMinimumDuration`.
   */
  minimumDurationHours: number;
  minimumDurationMinutes: number;
  allowsBiweeklyFrequency: boolean;
  /** `20` para 1.2x, `8` para 1.08x, `0` para 1.0x — inteiro, para texto. */
  priceSurchargePercent: number;
}

/**
 * O que o binário antigo (`isConventional ? … : …`) dava para qualquer não-convencional. É o
 * fallback do **formulário** quando a categoria escolhida não tem linha no catálogo — a tela de
 * categoria omite essas (Spec §5.1), mas um rascunho no atom ainda pode referenciar uma, e ela é
 * uma parceria (a Saludia não existe no banco de dev).
 *
 * Não é um fallback geral: quem pergunta pelas regras da convencional (a tela de slots) não pode
 * receber regras de parceria como resposta. Por isso `resolveModalityRules` devolve `undefined` e
 * cada chamador decide o que fazer sem linha.
 */
export const PARTNERSHIP_FALLBACK_RULES: ReservedScheduleModalityRules = {
  minimumDuration: "01:00",
  minimumDurationHours: 1,
  minimumDurationMinutes: 0,
  allowsBiweeklyFrequency: true,
  priceSurchargePercent: 0,
};

/**
 * Traduz a linha do catálogo nas regras que o formulário, o drawer do termo e a tela de slots
 * consomem (KRA-523). Substitui as constantes `LIVANCE_*`/`PARTNERSHIPS_*` e o `isConventional`
 * como proxy de regra: a fonte é a linha da modalidade, e uma parceria que divergir (Careplus,
 * 1.08x) deixa de herdar regra de outra.
 *
 * Pura: sem linha, devolve `undefined` e não loga — roda a cada render dos componentes, e o log
 * (quando cabe) é do chamador, uma vez por troca de categoria.
 */
export const resolveModalityRules = (
  catalog: ModalityCatalogRow[],
  codModalidadePeriodoGarantido: number,
): ReservedScheduleModalityRules | undefined => {
  const row = catalog.find(
    (catalogued) =>
      catalogued.codModalidadePeriodoGarantido === codModalidadePeriodoGarantido,
  );

  if (!row) {
    return undefined;
  }

  const [hours, minutes] = row.duracaoMinimaDoPeriodo.split(":");

  return {
    minimumDuration: `${hours}:${minutes}`,
    minimumDurationHours: Number(hours),
    minimumDurationMinutes: Number(minutes),
    allowsBiweeklyFrequency: row.permiteFrequenciaQuinzenal,
    // 1.08 - 1 em float é 0.08000000000000007 — sem o round, o drawer diria "8.000000000000007%".
    priceSurchargePercent: Math.round((row.multiplicadorPreco - 1) * 100),
  };
};

/** `"04:00"` → `"4 horas"`, `"01:00"` → `"1 hora"`, `"01:30"` → `"1h30"`. */
export const durationPhrase = (duration: string): string => {
  const [hours, minutes] = duration.split(":").map(Number);

  if (minutes) {
    return `${hours}h${String(minutes).padStart(2, "0")}`;
  }

  return `${hours} ${hours === 1 ? "hora" : "horas"}`;
};

const frequencyPhrase = (rules: ReservedScheduleModalityRules): string =>
  rules.allowsBiweeklyFrequency ? "semanal ou quinzenal" : "semanal";

/** Bloco "Modelo de precificação" do drawer do termo. */
export const pricingDescription = (rules: ReservedScheduleModalityRules): string => {
  const base =
    "Pague o valor das horas reservadas, considerando o preço estabelecido em contrato";

  return rules.priceSurchargePercent > 0
    ? `${base}, acrescido de ${rules.priceSurchargePercent}%.`
    : `${base}.`;
};

/** Bloco "Tempo mínimo de reserva" do drawer do termo. */
export const minimumBookingDescription = (
  rules: ReservedScheduleModalityRules,
): string => {
  const duration = durationPhrase(rules.minimumDuration);
  const consecutive = duration === "1 hora" ? "consecutiva" : "consecutivas";

  return `Crie períodos garantidos de no mínimo ${duration} ${consecutive} e com frequência ${frequencyPhrase(rules)}.`;
};

/**
 * Complemento dos alertas da tela de slots que apontam para a convencional. **Faz parte da cópia,
 * não é enfeite:** o seed da Porto tem uma janela noturna de 3h, e por contenção total um PG de 4h
 * nunca cabe nela — enquanto 4h é o mínimo da convencional. Ou seja, para um horário noturno acima
 * de 3h não existe combinação válida, e o CTA leva a um formulário que não devolve horário nenhum.
 * Dizer o que muda é o que evita o profissional gastar dois formulários para descobrir isso.
 */
export const slotsConstraintDescription = (
  rules: ReservedScheduleModalityRules,
): string =>
  `nela a duração mínima é de ${durationPhrase(rules.minimumDuration)}, com frequência ${frequencyPhrase(rules)}`;
